import { pushEvent } from './gtm';

/**
 * Event categories for tracking
 */
export enum EventCategory {
  USER = 'user',
  WORKSPACE = 'workspace',
  NAVIGATION = 'navigation',
  FORM = 'form',
  SUBSCRIPTION = 'subscription',
  SEARCH = 'search',
}

/**
 * Event actions for tracking
 */
export enum EventAction {
  // User actions
  LOGIN = 'login',
  SIGNUP = 'signup',
  LOGOUT = 'logout',
  
  // Workspace actions
  VIEW = 'view',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  CONTACT = 'contact',
  SHARE = 'share',
  
  // Navigation actions
  PAGE_VIEW = 'page_view',
  WHATSAPP_CHAT_CLICK = 'whatsapp_chat_click',
  CLICK = 'click',
  
  // Form actions
  SUBMIT = 'submit',
  ERROR = 'error',
  
  // Subscription actions
  SUBSCRIBE = 'subscribe',
  UNSUBSCRIBE = 'unsubscribe',
  PAYMENT = 'payment',
  
  // Search actions
  SEARCH = 'search',
  FILTER = 'filter',
}

/**
 * Interface for event data
 */
interface EventData {
  category: EventCategory;
  action: EventAction;
  label?: string;
  value?: number;
  [key: string]: unknown;
}

/**
 * Track an event with Google Tag Manager
 * @param eventData - The event data to track
 */
export function trackEvent(eventData: EventData): void {
  pushEvent({
    event: 'custom_event',
    ...eventData,
  });
}

/**
 * Track a page view
 * @param pagePath - The path of the page
 * @param pageTitle - The title of the page
 */
export function trackPageView(
  pagePath: string,
  pageTitle: string,
  extra?: Record<string, string | number | boolean>,
): void {
  pushEvent({
    event: 'page_view',
    page_path: pagePath,
    page_title: pageTitle,
    ...extra,
  });
}

/**
 * Track a user login
 * @param method - The login method used (e.g., 'email', 'google', 'facebook')
 */
export function trackLogin(method: string): void {
  trackEvent({
    category: EventCategory.USER,
    action: EventAction.LOGIN,
    label: method,
  });
}

/**
 * Track a form submission
 * @param formName - The name of the form
 * @param success - Whether the submission was successful
 */
export function trackFormSubmission(formName: string, success: boolean): void {
  trackEvent({
    category: EventCategory.FORM,
    action: EventAction.SUBMIT,
    label: formName,
    success,
  });
}

/**
 * Track a workspace view
 * @param workspaceId - The ID of the workspace
 * @param workspaceTitle - The title of the workspace
 */
export function trackWorkspaceView(workspaceId: string, workspaceTitle: string): void {
  trackEvent({
    category: EventCategory.WORKSPACE,
    action: EventAction.VIEW,
    label: workspaceTitle,
    workspace_id: workspaceId,
  });
}

/**
 * Track a search
 * @param query - The search query
 * @param resultsCount - The number of results
 */
export function trackSearch(query: string, resultsCount: number): void {
  trackEvent({
    category: EventCategory.SEARCH,
    action: EventAction.SEARCH,
    label: query,
    value: resultsCount,
  });
}